import * as moment from 'moment';

import { PreautorisationState, PreautorisationStateModel } from './preautorisation.state';
import { find, get, orderBy, pick } from 'lodash';

import { AccepterProjetDataModel } from '../models/accepter-projet.model';
import { DepensesResidenceCouranteModel } from '../models/depenses-residence-courante.model';
import { ErreurValidationModel } from '../models/erreur-validation.modele';
import { FrequenceEnum } from '../../../shared/constants/frequence.config';
import { RecapDataModel } from '../models/recap-data.model';
import { Selector } from '@ngxs/store';
import { StatusPreautorisation } from '../../../shared/modeles/variables';
import { StepEnum } from '../constants/step.enum';
import { TauxGarantiModel } from '../models/taux-garanti.model';
import { TypeEmprunteurEnum } from '../constants/type-emprunteur.enum';
import { TypeMultiplexEnum } from '../constants/type-multiplex.enum';
import { TypeMultiproprietaireEnum } from '../constants/type-multiproprietaire.enum';
import { TypeOffreEnum } from '../../shared/constants/type-offre.enum';
import { TypeProjet } from '../../../shared/constants/type-projet.enum';
import { TypeProprieteEnum } from '../constants/type-propriete.enum';

export class PreautorisationSelectors {
  codeFrequencePaiementHypothecaire;
  @Selector([PreautorisationState])
  static isLoaded(state: PreautorisationStateModel): boolean {
    return state.isLoaded;
  }

  @Selector([PreautorisationState])
  static isModifying(state: PreautorisationStateModel): boolean {
    return state.isModifying;
  }

  @Selector([PreautorisationState])
  static isAchatADeux(state: PreautorisationStateModel): boolean | null {
    return get(state, 'preautorisation.indCoemprunteurProprieteActuelle', null);
  }

  @Selector([PreautorisationState])
  static isCoemprunteur(state: PreautorisationStateModel): boolean | null {
    return get(state, 'preautorisation.indCoemprunteur', null);
  }

  @Selector([PreautorisationState])
  static prenomCoemprunteur(state: PreautorisationStateModel): string | null {
    return get(state, 'preautorisation.prenomCoemprunteur', null);
  }

  @Selector([PreautorisationState])
  static isEmprunteurPrincipal(state: PreautorisationStateModel): boolean | null {
    return get(state, 'preautorisation.indCoemprunteurPrincipal', null);
  }

  @Selector([PreautorisationSelectors.isCoemprunteur, PreautorisationSelectors.isEmprunteurPrincipal])
  static isEmprunteurSecondaire(isCoemprunteur: boolean, isEmprunteurPrincipal: boolean): boolean | null {
    return isCoemprunteur && !isEmprunteurPrincipal;
  }

  @Selector([PreautorisationState])
  static typeEmprunteur(state: PreautorisationStateModel): TypeEmprunteurEnum | null {
    if (!state.preautorisation.indCoemprunteur && state.preautorisation.indCoemprunteurPrincipal) {
      return TypeEmprunteurEnum.Unique;
    }

    if (state.preautorisation.indCoemprunteurPrincipal && state.preautorisation.indCoemprunteur) {
      return TypeEmprunteurEnum.Coemprunteur1;
    }

    if (state.preautorisation.indCoemprunteur && !state.preautorisation.indCoemprunteurPrincipal) {
      return TypeEmprunteurEnum.Coemprunteur2;
    }
  }

  @Selector([PreautorisationState])
  static isMemeCoemprunteurProprieteActuelle(state: PreautorisationStateModel): boolean | null {
    return get(state, 'preautorisation.indMemeCoemprunteurProprieteActuelle', null);
  }

  @Selector([PreautorisationState])
  static courrielCoemprunteur(state: PreautorisationStateModel): string | null {
    return get(state, 'preautorisation.courrielCoemprunteur', null);
  }

  @Selector([PreautorisationState])
  static codeInvitation(state: PreautorisationStateModel): string | null {
    return get(state, 'preautorisation.codeInvitation', null);
  }

  @Selector([PreautorisationState])
  static dateExpirationCodeInvitation(state: PreautorisationStateModel): Date | null {
    return moment(get(state, 'preautorisation.codeExpiration', null))
      .utc()
      .toDate();
  }

  @Selector([PreautorisationState])
  static statutPreautorisation(state: PreautorisationStateModel): StatusPreautorisation {
    return get(state, 'preautorisation.statut', null);
  }

  @Selector([PreautorisationState])
  static nbrLogements(state: PreautorisationStateModel): number | null {
    return get(state, 'preautorisation.nbrLogements', null);
  }

  @Selector([PreautorisationState])
  static typePropriete(state: PreautorisationStateModel): TypeProprieteEnum | null {
    return get(state, 'typePropriete', null);
  }

  @Selector([PreautorisationState])
  static typeMultiplex(state: PreautorisationStateModel): TypeMultiplexEnum | null {
    return get(state, 'typeMultiplex', null);
  }

  @Selector([PreautorisationState])
  static isResidencePrincipale(state: PreautorisationStateModel): 'O' | 'N' | null {
    return state.isResidencePrincipale;
  }

  @Selector([PreautorisationState])
  static isCitoyenCanadien(state: PreautorisationStateModel): 'O' | 'N' | null {
    return state.isCitoyenCanadien;
  }

  @Selector([PreautorisationState])
  static typePreautorisation(state: PreautorisationStateModel): TypeProjet | null {
    return get(state, 'preautorisation.type', null);
  }

  @Selector([PreautorisationState])
  static isSalarie(state: PreautorisationStateModel): 'O' | 'N' | null {
    return state.isSalarie;
  }

  @Selector([PreautorisationState])
  static typeMultiproprietaire(state: PreautorisationStateModel): TypeMultiproprietaireEnum | null {
    return state.typeMultiproprietaire;
  }

  @Selector([PreautorisationState])
  static revenuBrut(state: PreautorisationStateModel): number | null {
    return get(state, 'preautorisation.emploi.revenuAnnuelBrut', null);
  }

  @Selector([PreautorisationState])
  static revenuLocatif(state: PreautorisationStateModel): number | null {
    return get(state, 'preautorisation.revenuLocatifEstime', null);
  }

  @Selector([PreautorisationState])
  static codeFrequenceRevenuLocatif(state: PreautorisationStateModel): FrequenceEnum | null {
    return get(state, 'preautorisation.codeFrequenceRevenuLocatifEstime', null);
  }

  @Selector([PreautorisationState])
  static miseDeFonds(state: PreautorisationStateModel): number | null {
    return get(state, 'preautorisation.miseDeFonds', null);
  }

  @Selector([PreautorisationState])
  static indHabitableHiver(state: PreautorisationStateModel): boolean | null {
    return get(state, 'preautorisation.indHabitableHiver', null);
  }

  @Selector([PreautorisationState])
  static coordonnees(state: PreautorisationStateModel) {
    return {
      adresseFormattee: get(state, 'preautorisation.coordonnees.adresseFormattee', null),
      courriel: get(state, 'preautorisation.coordonnees.courriel', null),
      numeroTelephone: get(state, 'preautorisation.coordonnees.numeroTelephone', null),
      adresseComplete: pick(get(state, 'preautorisation.coordonnees', null), [
        'id',
        'numeroCivique',
        'rue',
        'typeUnite',
        'unite',
        'ville',
        'territoire',
        'pays',
        'codePostal',
        'adresseFormattee'
      ])
    };
  }

  @Selector([PreautorisationState])
  static currentStep(state: PreautorisationStateModel): StepEnum | null {
    return state.currentStep;
  }

  @Selector([PreautorisationState])
  static donneesHypotheque(state: PreautorisationStateModel): DepensesResidenceCouranteModel {
    return get(state, 'preautorisation.depensesResidenceCourante', null) || {};
  }

  @Selector([PreautorisationState])
  static prixPropriete(state: PreautorisationStateModel): number | null {
    return state.preautorisation.statut === StatusPreautorisation.ContreOffreAcceptee
      ? get(state, 'preautorisation.resultat.montantContreOffre', null)
      : get(state, 'preautorisation.montantPropriete', null);
  }

  @Selector([
    PreautorisationSelectors.revenuBrut,
    PreautorisationSelectors.donneesHypotheque,
    PreautorisationSelectors.coordonnees,
    PreautorisationSelectors.prixPropriete,
    PreautorisationSelectors.miseDeFonds,
    PreautorisationSelectors.isCoemprunteur,
    PreautorisationSelectors.typePreautorisation,
    PreautorisationSelectors.courrielCoemprunteur,
    PreautorisationSelectors.isMemeCoemprunteurProprieteActuelle,
    PreautorisationSelectors.nbrLogements,
    PreautorisationSelectors.revenuLocatif,
    PreautorisationSelectors.codeFrequenceRevenuLocatif,
    PreautorisationSelectors.indHabitableHiver
  ])
  static donneesRecap(
    revenuAnnuelBrut: number,
    depensesResidenceCourante: DepensesResidenceCouranteModel,
    coordonnees: { numeroTelephone: string; courriel: string; adresseFormattee: string },
    montantPropriete: number,
    miseDeFonds: number,
    isCoemprunteur: boolean,
    typePreautorisation: TypeProjet,
    courrielCoemprunteur: string,
    isMemeCoemprunteurProprieteActuelle: boolean,
    nbrLogements: number,
    revenuLocatif: number,
    codeFrequenceRevenuLocatif: FrequenceEnum,
    indHabitableHiver: boolean | null
  ): RecapDataModel {
    return {
      revenuAnnuelBrut,
      miseDeFonds,
      montantPropriete,
      isMemeCoemprunteurProprieteActuelle,
      depensesResidenceCourante: {
        ...depensesResidenceCourante,
        montantFraisCopropriete: depensesResidenceCourante.montantFraisCopropriete || 0,
        montantPaiementHypothecaire: depensesResidenceCourante.montantPaiementHypothecaire || 0
      },
      coordonnees,
      isCoemprunteur,
      typePreautorisation,
      courrielCoemprunteur,
      nbrLogements,
      revenuLocatifEstime: {
        montant: revenuLocatif,
        frequence: codeFrequenceRevenuLocatif
      },
      indHabitableHiver
    };
  }

  @Selector([
    PreautorisationSelectors.miseDeFonds,
    PreautorisationSelectors.prixPropriete,
    PreautorisationSelectors.prenomCoemprunteur,
    PreautorisationSelectors.dateExpirationCodeInvitation,
    PreautorisationSelectors.typePreautorisation,
    PreautorisationSelectors.donneesHypotheque,
    PreautorisationSelectors.isMemeCoemprunteurProprieteActuelle,
    PreautorisationSelectors.isCoemprunteur,
    PreautorisationSelectors.nbrLogements,
    PreautorisationSelectors.revenuLocatif,
    PreautorisationSelectors.codeFrequenceRevenuLocatif,
    PreautorisationSelectors.indHabitableHiver
  ])
  static donneesAccepterProjet(
    miseDeFonds: number,
    prixPropriete: number,
    prenomCoemprunteur: string,
    dateExpirationCodeInvitation: Date,
    typePreautorisation: TypeProjet,
    depensesResidenceCourante: DepensesResidenceCouranteModel,
    isMemeCoemprunteurProprieteActuelle: boolean,
    isCoemprunteur: boolean,
    nbrLogements: number,
    revenuLocatif: number,
    codeFrequenceRevenuLocatif: FrequenceEnum,
    indHabitableHiver: boolean | null
  ): AccepterProjetDataModel {
    return {
      miseDeFonds,
      prixPropriete,
      prenomCoemprunteur,
      dateExpirationCodeInvitation,
      typePreautorisation,
      depensesResidenceCourante: {
        ...depensesResidenceCourante,
        montantFraisCopropriete: depensesResidenceCourante.montantFraisCopropriete || 0,
        montantPaiementHypothecaire: depensesResidenceCourante.montantPaiementHypothecaire || 0
      },
      isMemeCoemprunteurProprieteActuelle,
      isCoemprunteur,
      nbrLogements,
      revenuLocatifEstime: {
        montant: revenuLocatif,
        frequence: codeFrequenceRevenuLocatif
      },
      indHabitableHiver
    };
  }

  @Selector([PreautorisationState])
  static isConsentementAccepted(state: PreautorisationStateModel): boolean {
    return !!state.idConsentement;
  }

  @Selector([PreautorisationState])
  static montantDemande(state: PreautorisationStateModel): number {
    return get(state, 'preautorisation.montantPropriete', 0) - get(state, 'preautorisation.miseDeFonds', 0);
  }

  @Selector([PreautorisationState])
  static montantPropose(state: PreautorisationStateModel): number {
    return get(state, 'preautorisation.resultat.montantContreOffre', 0) - get(state, 'preautorisation.miseDeFonds', 0);
  }

  @Selector([PreautorisationSelectors.prixPropriete, PreautorisationSelectors.miseDeFonds])
  static montantPreautorise(prixPropriete: number, miseDeFonds: number): number {
    return Math.max(prixPropriete - miseDeFonds, 0);
  }

  @Selector([PreautorisationState])
  static tauxPreautorisation(state: PreautorisationStateModel): Array<TauxGarantiModel> {
    return orderBy(
      get(state, 'preautorisation.resultat.taux', []).filter(t => t.typeOffre === TypeOffreEnum.OffreNumerique),
      ['indPromotion', 'sousTypeTaux', 'frequenceTerme', 'nbrUniteTerme'],
      [
        'desc' /* promo en premier */,
        'asc' /* Fixe avant Variable */,
        'desc' /* terme en Mois avant Ans*/,
        'asc' /* plus petit terme en premier */
      ]
    );
  }

  @Selector([PreautorisationState])
  static garantieTaux(state: PreautorisationStateModel): TauxGarantiModel {
    return get(state, 'preautorisation.resultat.taux', []).find(t => t.typeOffre === TypeOffreEnum.OffreAutreCanal);
  }

  @Selector([PreautorisationSelectors.tauxPreautorisation, PreautorisationSelectors.statutPreautorisation])
  static isRedirectionPageFonctionInactiveTaux(taux: Array<TauxGarantiModel>, statut: StatusPreautorisation): boolean {
    return taux.length === 0 && statut === StatusPreautorisation.EnAttenteSelectionTaux;
  }

  @Selector([PreautorisationState])
  static tauxSelectionne(state: PreautorisationStateModel): TauxGarantiModel {
    return find(get(state, 'preautorisation.resultat.taux', []), 'indSelectionMembre');
  }

  @Selector([PreautorisationSelectors.tauxPreautorisation])
  static tauxPreautorisationPromotion(taux: Array<TauxGarantiModel>): Array<TauxGarantiModel> {
    return taux.filter(t => t.indPromotion);
  }

  @Selector([PreautorisationState])
  static preautorisationId(state: PreautorisationStateModel): string {
    return get(state, 'preautorisation.id', '');
  }

  @Selector([PreautorisationState])
  static dateAnnulationContreOffre(state: PreautorisationStateModel): Date {
    return moment(get(state.preautorisation, 'resultat.dateExpirationContreOffre', null))
      .utc()
      .add(1, 'day')
      .toDate();
  }

  @Selector([PreautorisationState])
  static dateExpirationSelectionTaux(state: PreautorisationStateModel): Date {
    return moment(get(state.preautorisation, 'dateExpirationSelectionTaux', null))
      .utc()
      .toDate();
  }

  @Selector([PreautorisationSelectors.tauxSelectionne])
  static dateExpirationTaux(taux: TauxGarantiModel): Date {
    return moment(taux.dateExpirationGarantieTaux)
      .utc()
      .toDate();
  }

  @Selector([PreautorisationState])
  static dateExpirationPreaut(state: PreautorisationStateModel): Date {
    const dateExpiration: string = get(state.preautorisation, 'resultat.dateExpiration', null);
    return moment(dateExpiration)
      .utc()
      .toDate();
  }

  @Selector([PreautorisationSelectors.statutPreautorisation, PreautorisationSelectors.prixPropriete])
  static isRefusPreadmissiblite(statut: StatusPreautorisation, prixPropriete: number): boolean {
    return statut === StatusPreautorisation.RefuseeSimplifie && prixPropriete == null;
  }

  @Selector([PreautorisationState])
  static validationError(state: PreautorisationStateModel): ErreurValidationModel {
    return get(state, 'validationError', null);
  }

  @Selector([PreautorisationState])
  static idCybermetrie(state: PreautorisationStateModel): string {
    return get(state.preautorisation, 'idCybermetrie', 'idTransactionInconnu');
  }
}
